import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ViewEntityType } from "@colab/shared";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { SavedView } from "./types";

// POST /api/admin/views/reorder
//
// Pattern C optimistic — the drag UX needs instant feedback. We snapshot
// the list cache, reorder it locally, and revert on error. The server
// re-runs per-row permission checks via the `reorder_views(text, uuid[])`
// SECURITY DEFINER RPC; 42501 errors map to the friendly permission
// message in `apps/api/src/services/views.ts`.

type Vars = { entityType: ViewEntityType; orderedIds: string[] };

export function useReorderViews() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, Vars>({
    mutationFn: ({ entityType, orderedIds }) =>
      apiFetch<{ ok: true }>("/admin/views/reorder", {
        method: "POST",
        body: JSON.stringify({ entityType, orderedIds }),
      }),
    onMutate: async ({ entityType, orderedIds }) => {
      const key = queryKeys.views.list(entityType);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<SavedView[]>(key) ?? null;
      if (previous) {
        const byId = new Map(previous.map((v) => [v.id, v]));
        const next = orderedIds
          .map((id, idx) => {
            const row = byId.get(id);
            return row ? { ...row, position: idx + 1 } : null;
          })
          .filter((x): x is SavedView => x !== null);
        // Append any rows the optimistic order didn't mention (defensive
        // — the dialog always sends the complete list).
        for (const v of previous) {
          if (!orderedIds.includes(v.id)) next.push(v);
        }
        queryClient.setQueryData(key, next);
      }
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      const ctx = context as { previous: SavedView[] | null; key: readonly unknown[] } | undefined;
      if (ctx?.previous) {
        queryClient.setQueryData(ctx.key, ctx.previous);
      }
      toast.error(err.message ?? "Failed to reorder views");
    },
    onSettled: (_data, _err, { entityType }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.views.list(entityType),
      });
    },
  });
}
